import { animate, style, transition, trigger } from '@angular/animations';
import { NgForOf, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef, MatRow, MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AdminService } from '../../admin/admin.service';
import { FeatureFlagService } from '../../feature-flags/feature-flag.service';
import { DataToolsApps } from '../../shared/DataToolsApps';
import { DataToolsLiteMessage } from '../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../shared/services/datatools-lite.service';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { UtilsService } from '../../shared/services/utils.service';
import { DataRulesConfirmDialogComponent } from '../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { IDataRule, IDataSource, ITask } from '../data-rules-model';
import { DataRulesService } from '../data-rules.service';
import { ErrorNotificationService } from '../error-notification.service';
import { RuleChainsDataService } from '../rule/rule-chain/rule-chain-data.service';

@Component({
  selector: 'app-examples-found',
  templateUrl: './examples-found.component.html',
  styleUrls: ['./examples-found.component.scss'],
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    NgForOf,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgIf,
    MatTooltip
  ],
  animations: [
    trigger('transitionLoading', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('.2s ease-out', style({ opacity: '1' })),
      ]),
      transition(':leave', [
        style({ opacity: '1' }),
        animate('.2s ease-out', style({ opacity: '0' })),
      ]),
    ]),
  ]
})

export class ExamplesFoundComponent implements OnChanges, AfterViewInit, OnInit {
  private readonly previewTextChangeEnabled = this.featureFlagService.enabled(FeatureFlagService.FLAGS.dataRulesPreviewTextChange);
  // TODO: remove once the non-Folders list view (dataRulesFolders flag off) is retired, along with the Router injection below.
  private readonly foldersEnabled = this.featureFlagService.enabled(FeatureFlagService.FLAGS.dataRulesFolders);

  @Input() currentRuleExamples;
  @Input() selectedEntity;
  @Input() entityMap;
  @Input() tasksInProgress;
  @Input() dataRule: IDataRule;
  @Output() appliedToBullhornEmitter = new EventEmitter<void>();
  dataSource: IDataSource[] = [];
  columnsToDisplay = ['fieldName', 'status'];
  inAChain: boolean;
  firstInAChain: boolean;

  @ViewChild('examplesFound')
  examplesFound: ElementRef;
  examplesLoading: boolean;
  total: number;

  fuzzy = false;
  fuzzySearchTooltip = 'Depending on your Bullhorn corporation configuration, find criteria in Note rules can use "contains" on text fields rather than an exact match. For example, for some corporations, a rule that finds Notes where action = "call" will also find Notes with action "Inbound Call" or "Outbound Call".';

  constructor(private dataRulesService: DataRulesService,
              private errorNotificationService: ErrorNotificationService,
              private dialog: MatDialog,
              private router: Router,
              private ruleChainsDataService: RuleChainsDataService,
              private utilsService: UtilsService,
              private adminService: AdminService,
              private datatoolsLiteService: DatatoolsLiteService,
              private messageNotificationService: MessageNotificationService,
              private featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    const chain = this.ruleChainsDataService.getChainByRuleId(this.dataRule.ruleData.id);

    for (const criteria of this.dataRule.ruleData.source.config.criteria) {
      this.fuzzy = this.fuzzy || (this.selectedEntity === 'Note' && criteria.fieldType === 'STRING');
    }

    if (!!chain) {
      this.inAChain = true;

      this.firstInAChain =
        chain.ruleIds.findIndex(ruleId => this.utilsService.compareInternalDataRuleIds(ruleId, this.dataRule.ruleData.id)) === 0;
    }
  }

  ngAfterViewInit() {
    this.examplesFound?.nativeElement.scrollIntoView({ behavior: 'smooth'});
  }

  ngOnChanges() {
    this.examplesLoading = true;
    this.examplesFound?.nativeElement.scrollIntoView({ behavior: 'smooth'});
    this.dataRulesService.getRuleSamples(this.dataRule.ruleData.id).subscribe((samples) => {

      // Table resets for a new set of table data
      this.dataSource = [];
      this.columnsToDisplay = ['fieldName', 'status'];

      const fieldToRowMap = new Map();
      this.total = samples.total;
      samples.updateSamples.forEach(us => {
        us.changedFields.forEach(cf => {
          if (!fieldToRowMap.has(cf.fieldName)) {
            const oldRow = {id: us.id, fieldName: cf.fieldName, status: 'Current',
              [us.id]: this.getDisplayValue(cf.oldValue, cf.fieldType)};
            const newRow = {id: us.id, fieldName: '', status: 'New', [us.id]: this.getDisplayValue(cf.newValue, cf.fieldType)};
            fieldToRowMap.set(cf.fieldName, {old: oldRow, new: newRow});
          } else {
            const row = fieldToRowMap.get(cf.fieldName);
            row.old[us.id] = this.getDisplayValue(cf.oldValue, cf.fieldType);
            row.new[us.id] = this.getDisplayValue(cf.newValue, cf.fieldType);
          }
        });
      });

      fieldToRowMap.forEach((row) => {
        this.dataSource.push(row.old);
        this.dataSource.push(row.new);
      });

      samples.updateSamples.forEach(us => {
        this.columnsToDisplay.push(us.id.toString());
      });
      this.examplesLoading = false;
    }, (error: HttpErrorResponse) => {
      this.errorNotificationService.error(`Failed to load sample results for ${this.dataRule.ruleData.name}`, error.error);
    });
  }

  applyToBullhorn(): void {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DATARULES_RUN_RULE);
      return;
    }

    const rule = this.currentRuleExamples;

    const dialogTitle = this.inAChain ? 'This is a chained rule, are you sure you want to apply these changes to Bullhorn?'
      : 'Are you sure you want to apply these changes to Bullhorn?';

    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '55vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'DataRules-KyloeIcon',
        title: dialogTitle,
        messageIcon: 'bhi-search',
        message: 'Rule name: ' + rule.name
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data)
        return;

      const task: ITask = {ruleId: rule.id, state: 'SUBMITTED'};
      console.log('Exporting to BH', task);
      this.dataRulesService.applyToBullhorn(task).subscribe({
        next: savedTask => {
          console.log('Applied to Bullhorn:', savedTask);

          if (!this.foldersEnabled()) {
            this.router.navigate([`/${DataToolsApps.HISTORY_DATARULES}`]);
            return;
          }

          this.messageNotificationService.success(
            'Your rule has been queued',
            `Please visit the <a href="/${DataToolsApps.HISTORY_DATARULES}">History page</a> to view progress`
          );
          this.appliedToBullhornEmitter.emit();
        },
        error: error => {
          this.errorNotificationService.error('Apply failure', error);
        }
      });
    });
  }

  get recordsFoundLabel(): string {
    return this.previewTextChangeEnabled() ? '# of Records Found:' : '# Records Affected:';
  }

  private getDisplayValue(value: any, fieldType: string): string {

    if (typeof value === 'object' && value !== null && value.hasOwnProperty('value')) {
        value = value.value;
    }

    if (value === null)
      return 'NULL';
    if (fieldType === 'DATE')
      return new Date(value.slice(0, -3)).toLocaleString(navigator.language);
    if (fieldType === 'TO_MANY')
      return value.toString().split(',').join(', ');
    if (fieldType === 'ADDRESS')
      return JSON.stringify(value, null, 2);
    return value;
  }
}
